import useBrands from "@/hooks/pages/brands/useBrands";
import { baseURL } from "@/services/api";
import { IBrands, IBrandsGeneric } from "global-types/global.types";
import { GetStaticProps } from "next";
import Link from "next/link";
import { useState } from "react";
import Row from "react-bootstrap/Row";
import "bootstrap/dist/css/bootstrap.min.css";
import styled from "styled-components";
import Head from "next/head";
import { t, localePrefix } from "@/helper/helper";
import Image from "next/image";
import { SITE_NAME } from "@/helper/constant";

interface Props {
  locale: string;
  brandData: IBrands<IBrandsGeneric[]>;
}

export default function Brands({ brandData, locale }: Props) {
  const [a, setA] = useState<string | null>(null);
  const [data, setData] = useState(brandData);

  useBrands({
    params: {
      a,
    },
    initialData: data,
    onSuccess: (data) => {
      setData(data);
    },
  });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("homePage", locale),
        item: `https://promusic.az${localePrefix(locale)}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("brands", locale),
        item: `https://promusic.az${localePrefix(locale)}/brand`,
      },
    ],
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t("brands", locale),
    description: `${t("brands", locale)} - Promusic.az`,
    url: `https://promusic.az${localePrefix(locale)}/brand`,
    numberOfItems: data?.data?.length || 0,
    itemListElement: data?.data?.map((brand, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `https://promusic.az${localePrefix(locale)}/brand/${brand.slug}`,
    })),
  };

  return (
    <>
      <Wrapper>
        <Head>
          <title>{SITE_NAME} Brand</title>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadcrumbSchema),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
          />
        </Head>
        <div className="brand">
          <div className="breadcrumb">
            <Link href={`${localePrefix(locale) || "/"}`}>{t("homePage", locale)}</Link>
            <span className="breadcrumb-seperator" />
            <Link href={`${localePrefix(locale)}/brand`}>{t("brands", locale)}</Link>
          </div>

          <AlphaBox>
            {[
              "A",
              "B",
              "C",
              "D",
              "E",
              "F",
              "G",
              "H",
              "I",
              "J",
              "K",
              "L",
              "M",
              "N",
              "O",
              "P",
              "Q",
              "R",
              "S",
              "T",
              "U",
              "V",
              "W",
              "X",
              "Y",
              "Z",
            ].map((item, index) => (
              <Alpha
                className="alpha"
                key={index.toString()}
                onClick={() => {
                  setA(item);
                }}
              >
                {item}
              </Alpha>
            ))}
          </AlphaBox>
          <div className="brand-content">
            <h1>{t("brands", locale)}</h1>
            <Row>
              {data?.data.length !== 0 ? (
                data?.data.map(({ slug, name, logo }, index) => (
                  <div
                    className="col-md-2 mb-2 wrap-col-brand"
                    key={index.toString() + name}
                  >
                    <Link href={`/brand/${slug}`} key={index.toString() + name}>
                      <Image width={100} height={100} src={logo} alt={name} />
                      {name}
                    </Link>
                  </div>
                ))
              ) : (
                <NotFound>No Result</NotFound>
              )}
            </Row>
          </div>
        </div>
      </Wrapper>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale } = ctx;
  const brandData = await fetch(baseURL + "/brand", {
    headers: {
      "x-localization": locale ? locale : "az",
    },
  });

  return {
    props: {
      brandData: await brandData.json(),
      locale,
    },
    revalidate: 10,
  };
};

const Alpha = styled.span`
  font-size: 15px;
  margin-left: 10px;
  cursor: pointer;
`;

const AlphaBox = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Wrapper = styled.div`
  .brand {
    width: 1287px;
    margin: 0 auto;
    padding-bottom: 7rem;

    .wrap-col-brand {
      height: 200px;
      display: flex;
      flex-direction: column;

      a {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        font-size: 17px;
        color: #1b1c1f;
      }

      img {
        width: 100px;
        object-fit: contain;
        object-position: center;
      }
    }

    @media screen and (max-width: 1199px) {
      width: 100%;
      padding: 0 16px;
    }

    .brand-img img {
      width: 122px;
      height: 124px;
      object-fit: contain;
      margin-bottom: 2.4rem;
    }

    .tabs {
      cursor: pointer;
      text-align: center;

      .tab {
        font-style: normal;
        font-weight: normal;
        font-size: 1.8rem;
        line-height: 100%;
        color: rgba(27, 28, 31, 0.25);
        text-align: center;
        width: 30%;
        display: inline-grid;
        // border-top: .1rem solid rgba(184, 190, 206, 0.25);
        // border-bottom: .1rem solid rgba(184, 190, 206, 0.25);
        padding: 1.4rem 0;

        &.active {
          color: #1b1c1f;
        }

        // &:nth-child(1) {
        //     text-align: right;
        // }

        // &:nth-child(2) {
        //     text-align: left;
        // }
      }
    }

    .about-content {
      margin-top: 36px;
      width: 812px;
      margin: auto;

      h5 {
        font-style: normal;
        font-weight: bold;
        font-size: 36px;
        line-height: 150%;
        color: #040941;
        margin-bottom: 24px;
      }

      p {
        font-style: normal;
        font-weight: 500;
        font-size: 20px;
        line-height: 30px;
        color: #989eaf;
      }
    }

    // .breadcrumb {
    //     display: none;
    // }

    .about-header {
      background: url("../../assets//img/account-bg.png");
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center center;
      width: 100%;
      height: 27rem;
      border-radius: 6px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;
      padding: 2.4rem;
      margin-bottom: 5.6rem;
      margin-top: 24px;

      .breadcrumb {
        padding-top: 0;
        display: block;

        a {
          color: #fff;
        }

        .breadcrumb-seperator {
          background-color: #fff;
        }
      }
    }

    .brand-content {
      margin-top: 5.6rem;

      h1 {
        text-align: center;
        font-weight: bold;
        margin-bottom: 30px;
        font-size: 36px;
      }

      .content {
        margin-top: 3.6rem;
        display: grid;
      }

      .brand-about-child {
        font-style: normal;
        font-weight: 400;
        font-size: 2rem;
        line-height: 30px;
        color: #989eaf;
        text-align: left;
        margin-bottom: 30px;
      }

      .brand-about {
        width: 812px;
        margin: auto;
        margin-bottom: 4.8rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        h5 {
          a {
            font-style: normal;
            font-weight: bold;
            font-size: 36px;
            line-height: 150%;
            color: #040941;
            margin-bottom: 2.4rem;
          }
        }

        p {
          font-style: normal;
          font-weight: 500;
          font-size: 20px;
          line-height: 30px;
          color: #989eaf;
        }

        a {
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          line-height: 100%;
          /* identical to box height, or 16px */

          letter-spacing: 0.02em;
          color: #444444;
        }
      }
    }

    .brands-products {
      grid-column: 1/-1;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      grid-gap: 2.4rem 1.6rem;
      align-content: flex-start;
    }
  }

  @media screen and (max-width: 992.9px) {
    .brand .brand-content .brand-about {
      width: 100%;
    }
  }

  @media screen and (max-width: 767.9px) {
    .brand {
      .brand-content {
        margin-top: 24px;

        .brand-about {
          width: 100%;
          margin-bottom: 24px;

          h5 {
            font-size: 2.8rem;
            margin-bottom: 2.4rem;
          }

          p {
            font-size: 1.6rem;
            font-weight: 500;
            line-height: 150%;
          }
        }
      }

      .brand-img img {
        width: 96px;
        height: 96px;
        margin-bottom: 1.6rem;
      }

      .tabs {
        .tab {
          width: 50%;
          font-size: 1.6rem;
        }
      }
    }
  }
`;

const NotFound = styled.div`
  font-size: 30px;
  text-align: center;
`;
